import {
  HTTP_INTERCEPTORS,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, Provider } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { isArray, isDefined, isInstanceOf, isObject } from 'simple-bool';

const ISO_DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+\-]\d{2}:\d{2})$/;

const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Checks whether a given value is an ISO 8601 date or date-time string.
 *
 * The value must be a string matching either a full date-time pattern or a date-only pattern
 * and must be parsable by the JavaScript Date API.
 *
 * @param value - Value to validate.
 * @returns True if the value is a valid ISO date/date-time string, otherwise false.
 */
export function isIsoDateString(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  if (value.length < 10 || value.length > 35) return false;

  if (ISO_DATE_TIME_REGEX.test(value) || ISO_DATE_ONLY_REGEX.test(value)) {
    const time = Date.parse(value);
    return !Number.isNaN(time);
  }
  return false;
}
/**
 * Converts a string value to a Date instance.
 *
 * @param value - Value expected to be a date string.
 * @throws Error if the value is not a string.
 * @returns Date created from the provided value.
 */
export function convertStringToDate(value: unknown) {
  if (typeof value !== 'string') {
    throw new Error('Cannot convert intercepted request property to date - porperty is not a string');
  }
  return new Date(value);
}

/**
 * Creates an HTTP interceptor provider that recursively traverses JSON-like
 * response bodies and applies a custom deserialization function to matching
 * values.
 *
 * @param isFound - Predicate that determines whether a value should be deserialized.
 * @param deserialize - Function that transforms a matched value.
 * @returns Angular provider configuring the mapping interceptor.
 */
export function provideMappingInterceptor(
  isFound: (v: unknown) => boolean,
  deserialize: (v: unknown) => any
): Provider {
  @Injectable()
  class MappingInterceptor implements HttpInterceptor {
    /**
     * Intercepts HTTP responses and applies recursive property transformation
     * on JSON-like bodies using the configured predicate and deserializer.
     *
     * @param request - Outgoing HTTP request.
     * @param next - Next interceptor in the chain.
     * @returns Observable stream of the (potentially transformed) HTTP event.
     */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      return next.handle(request).pipe(
        map((event: HttpEvent<any>) => {
          if (!(event instanceof HttpResponse)) {
            return event;
          }

          const body = event.body;

          if (!this._isJsonLike(body)) {
            return event;
          }

          const transformed = this._convertProps(body);
          return event.clone({ body: transformed });
        })
      );
    }

    /**
     * Checks whether the provided value is an instance of common built-in
     * classes that should not be treated as plain JSON objects.
     *
     * @param value - Value to inspect.
     * @returns True if the value is a common class instance, otherwise false.
     */
    private _isCommonClassObject(value: any): boolean {
      return (
        isInstanceOf(value, Date) ||
        isInstanceOf(value, Blob) ||
        isInstanceOf(value, File) ||
        isInstanceOf(value, FormData) ||
        isInstanceOf(value, ArrayBuffer)
      );
    }

    /**
     * Determines if the value is JSON-like (plain object or array) and not
     * a special class instance.
     *
     * @param value - Value to inspect.
     * @returns True if the value is JSON-like, otherwise false.
     */
    private _isJsonLike(value: any): boolean {
      if (!isDefined(value)) return false;
      if (isArray(value)) return true;
      if (isObject(value)) {
        if (this._isCommonClassObject(value)) {
          return false;
        }
        return true;
      }
      return false;
    }

    /**
     * Recursively walks through arrays and plain objects, applying the
     * `deserialize` function to values that satisfy the `isFound` predicate.
     *
     * A WeakSet is used to track already-visited objects to prevent infinite
     * recursion in the presence of circular references.
     *
     * @typeParam T - Type of the input and output value.
     * @param input - Value to transform.
     * @param seen - WeakSet of already processed objects.
     * @returns Transformed value with matching properties deserialized.
     */
    private _convertProps<T>(input: T, seen = new WeakSet<object>()): T {
      if (!isDefined(input)) return input;

      if (isFound(input)) {
        return deserialize(input);
      }

      if (isArray(input)) {
        return input.map(v => this._convertProps(v, seen)) as unknown as T;
      }

      if (isObject(input)) {
        if (this._isCommonClassObject(input)) {
          return input;
        }

        if (seen.has(input as unknown as object)) {
          return input;
        }
        seen.add(input as unknown as object);

        const output: any = Object.create(Object.getPrototypeOf(input));
        for (const [key, value] of Object.entries(input as any)) {
          output[key] = this._convertProps(value, seen);
        }
        return output;
      }

      return input;
    }
  }

  return {
    provide: HTTP_INTERCEPTORS,
    multi: true,
    useClass: MappingInterceptor,
  };
}
